import React from "react";
import {
    FiX,
    FiTruck,
    FiCalendar,
    FiFileText,
    FiBox,
    FiPlus,
    FiTrash2,
    FiUser,
    FiInfo,
    FiArrowRight,
} from "react-icons/fi";

import DefaultLayout from "../../../../layouts/default";
import {
    DeliveryCheckProvider,
    useDeliveryCheck,
} from "./context/DeliveryCheckContext";

import { SectionHeader, FieldLabel, TextInput, SelectInput } from '../components/DeliveryFields';

function DeliveryProgress() {
    const { cancel, isSaving } = useDeliveryCheck();
    return (
        <div className="border-b border-slate-200 bg-white px-6 py-4">
            <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-indigo-600 text-white">
                    <FiTruck className="h-6 w-6" />
                </div>

                <div className="min-w-0 flex-1">
                    <h1 className="text-xl font-bold text-slate-900">
                        Delivery Check
                    </h1>

                    <p className="text-sm font-semibold text-indigo-600">
                        Step 1 of 2: Delivery Details & Traceability
                    </p>

                    <p className="mt-0.5 text-xs text-slate-500">
                        Record key delivery information to ensure full
                        traceability before completing the delivery check.
                    </p>
                </div>

                <button
                    type="button"
                    onClick={cancel}
                    disabled={isSaving}
                    aria-label="Back to deliveries"
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                >
                    <FiX className="h-5 w-5" />
                </button>
            </div>

            <div className="mx-auto mt-4 flex max-w-2xl items-center justify-center">
                <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-indigo-600 text-sm font-bold text-white">
                        1
                    </div>

                    <div>
                        <p className="text-xs font-bold text-indigo-600">
                            Delivery Details & Traceability
                        </p>
                        <p className="text-[11px] text-slate-500">
                            Supplier, products and reference information
                        </p>
                    </div>
                </div>

                <div className="mx-5 h-px w-16 bg-indigo-400" />

                <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-200 text-sm font-bold text-slate-500">
                        2
                    </div>

                    <div>
                        <p className="text-xs font-bold text-slate-700">
                            Delivery Check
                        </p>
                        <p className="text-[11px] text-slate-500">
                            Inspect items, temperatures and condition
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

function SupplierInformation() {
    const { delivery, updateDelivery } = useDeliveryCheck();

    return (
        <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <SectionHeader
                icon={FiUser}
                title="Supplier Information"
            />

            <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-2">
                <div>
                    <FieldLabel required>Supplier</FieldLabel>

                    <SelectInput
                        value={delivery.supplier}
                        onChange={(e) =>
                            updateDelivery("supplier", e.target.value)
                        }
                    >
                        <option value="">Select supplier</option>
                        <option>ABC Foods Ltd</option>
                        <option>Fresh Foods Supplier</option>
                        <option>London Food Services</option>
                        <option>Other Supplier</option>
                    </SelectInput>
                </div>

                <div>
                    <FieldLabel>
                        Supplier Contact (optional)
                    </FieldLabel>

                    <TextInput
                        value={delivery.supplierContact}
                        onChange={(e) =>
                            updateDelivery(
                                "supplierContact",
                                e.target.value
                            )
                        }
                    />
                </div>
            </div>
        </section>
    );
}

function DeliveryInformation() {
    const { delivery, updateDelivery } = useDeliveryCheck();

    return (
        <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <SectionHeader
                icon={FiFileText}
                title="Delivery Information"
            />

            <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-3">
                <div>
                    <FieldLabel required>
                        Delivery Date & Time
                    </FieldLabel>

                    <TextInput
                        type="datetime-local"
                        value={delivery.deliveryDateTime}
                        onChange={(e) =>
                            updateDelivery(
                                "deliveryDateTime",
                                e.target.value
                            )
                        }
                    />
                </div>

                <div>
                    <FieldLabel required>
                        Delivery Note / Reference No.
                    </FieldLabel>

                    <TextInput
                        value={delivery.deliveryNote}
                        onChange={(e) =>
                            updateDelivery(
                                "deliveryNote",
                                e.target.value
                            )
                        }
                    />
                </div>

                <div>
                    <FieldLabel>PO Number (optional)</FieldLabel>

                    <TextInput
                        value={delivery.poNumber}
                        onChange={(e) =>
                            updateDelivery(
                                "poNumber",
                                e.target.value
                            )
                        }
                    />
                </div>

                <div>
                    <FieldLabel>
                        Vehicle Registration (optional)
                    </FieldLabel>

                    <TextInput
                        value={delivery.vehicleRegistration}
                        onChange={(e) =>
                            updateDelivery(
                                "vehicleRegistration",
                                e.target.value
                            )
                        }
                    />
                </div>

                <div>
                    <FieldLabel>Driver Name (optional)</FieldLabel>

                    <TextInput
                        value={delivery.driverName}
                        onChange={(e) =>
                            updateDelivery(
                                "driverName",
                                e.target.value
                            )
                        }
                    />
                </div>

                <div>
                    <FieldLabel>Driver Contact (optional)</FieldLabel>

                    <TextInput
                        value={delivery.driverContact}
                        onChange={(e) =>
                            updateDelivery(
                                "driverContact",
                                e.target.value
                            )
                        }
                    />
                </div>
            </div>
        </section>
    );
}

function ProductsTable() {
    const {
        products,
        updateProduct,
        addProduct,
        removeProduct,
    } = useDeliveryCheck();

    return (
        <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <SectionHeader
                icon={FiBox}
                title="Products & Traceability"
                description="Add all product items in this delivery. Each item can have its own batch and use-by date."
            >
                <button
                    type="button"
                    onClick={addProduct}
                    className="flex items-center gap-2 rounded-lg border border-indigo-200 bg-white px-3 py-2 text-xs font-semibold text-indigo-600 hover:bg-indigo-50"
                >
                    <FiPlus className="h-4 w-4" />
                    Add Another Product
                </button>
            </SectionHeader>

            <div className="overflow-x-auto">
                <table className="w-full min-w-[1050px] text-left">
                    <thead>
                        <tr className="border-b border-slate-200 bg-slate-50">
                            <th className="w-10 px-3 py-3 text-[10px] font-bold text-slate-600">
                                #
                            </th>

                            <th className="px-2 py-3 text-[10px] font-bold text-slate-600">
                                Product Category *
                            </th>

                            <th className="px-2 py-3 text-[10px] font-bold text-slate-600">
                                Product Description
                            </th>

                            <th className="px-2 py-3 text-[10px] font-bold text-slate-600">
                                Quantity *
                            </th>

                            <th className="px-2 py-3 text-[10px] font-bold text-slate-600">
                                Unit
                            </th>

                            <th className="px-2 py-3 text-[10px] font-bold text-slate-600">
                                Batch / Lot No. *
                            </th>

                            <th className="px-2 py-3 text-[10px] font-bold text-slate-600">
                                Use-by Date *
                            </th>

                            <th className="px-2 py-3 text-[10px] font-bold text-slate-600">
                                Best Before
                            </th>

                            <th className="w-12 px-2 py-3 text-[10px] font-bold text-slate-600">
                                Action
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        {products.map((product, index) => (
                            <tr
                                key={product.id}
                                className="border-b border-slate-100 last:border-0"
                            >
                                <td className="px-3 py-3 text-xs text-slate-500">
                                    {index + 1}
                                </td>

                                <td className="px-2 py-2">
                                    <SelectInput
                                        value={product.category}
                                        onChange={(e) =>
                                            updateProduct(
                                                product.id,
                                                "category",
                                                e.target.value
                                            )
                                        }
                                    >
                                        <option value="">
                                            Select category
                                        </option>
                                        <option>
                                            Meat & Poultry
                                        </option>
                                        <option>
                                            Fruit & Vegetables
                                        </option>
                                        <option>
                                            Dairy Products
                                        </option>
                                        <option>
                                            Frozen Foods
                                        </option>
                                        <option>
                                            Bakery
                                        </option>
                                    </SelectInput>
                                </td>

                                <td className="px-2 py-2">
                                    <TextInput
                                        value={product.description}
                                        onChange={(e) =>
                                            updateProduct(
                                                product.id,
                                                "description",
                                                e.target.value
                                            )
                                        }
                                    />
                                </td>

                                <td className="w-20 px-2 py-2">
                                    <TextInput
                                        type="number"
                                        value={product.quantity}
                                        onChange={(e) =>
                                            updateProduct(
                                                product.id,
                                                "quantity",
                                                e.target.value
                                            )
                                        }
                                    />
                                </td>

                                <td className="w-24 px-2 py-2">
                                    <SelectInput
                                        value={product.unit}
                                        onChange={(e) =>
                                            updateProduct(
                                                product.id,
                                                "unit",
                                                e.target.value
                                            )
                                        }
                                    >
                                        <option>kg</option>
                                        <option>units</option>
                                        <option>litres</option>
                                        <option>boxes</option>
                                        <option>cases</option>
                                    </SelectInput>
                                </td>

                                <td className="px-2 py-2">
                                    <TextInput
                                        value={product.batchNumber}
                                        onChange={(e) =>
                                            updateProduct(
                                                product.id,
                                                "batchNumber",
                                                e.target.value
                                            )
                                        }
                                    />
                                </td>

                                <td className="px-2 py-2">
                                    <TextInput
                                        type="date"
                                        aria-label={`Use-by Date for product ${index + 1}`}
                                        required
                                        max="9999-12-31"
                                        value={product.useByDate}
                                        onChange={(e) =>
                                            updateProduct(
                                                product.id,
                                                "useByDate",
                                                e.target.value
                                            )
                                        }
                                    />
                                </td>

                                <td className="px-2 py-2">
                                    <TextInput
                                        type="date"
                                        aria-label={`Best Before for product ${index + 1}`}
                                        max="9999-12-31"
                                        value={product.bestBefore}
                                        onChange={(e) =>
                                            updateProduct(
                                                product.id,
                                                "bestBefore",
                                                e.target.value
                                            )
                                        }
                                    />
                                </td>

                                <td className="px-2 py-2 text-center">
                                    <button
                                        type="button"
                                        onClick={() =>
                                            removeProduct(product.id)
                                        }
                                        className="rounded-md p-2 text-slate-500 hover:bg-red-50 hover:text-red-600"
                                    >
                                        <FiTrash2 className="h-4 w-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

function AdditionalInformation() {
    const { delivery, updateDelivery, attachments, setAttachments } = useDeliveryCheck();

    return (
        <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <SectionHeader
                icon={FiFileText}
                title="Additional Information"
            />

            <div className="grid grid-cols-1 gap-5 p-5 md:grid-cols-2">
                <div>
                    <FieldLabel>
                        Delivery Instructions / Notes (optional)
                    </FieldLabel>

                    <textarea
                        value={delivery.notes}
                        onChange={(e) =>
                            updateDelivery("notes", e.target.value)
                        }
                        maxLength={500}
                        rows={4}
                        placeholder="Add any additional information about this delivery (e.g. special instructions, multiple pallets, etc.)"
                        className="w-full resize-none rounded-lg border border-slate-200 px-3 py-3 text-sm text-slate-800 outline-none placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                    />

                    <div className="mt-1 text-right text-[10px] text-slate-400">
                        {delivery.notes.length}/500
                    </div>
                </div>

                <div>
                    <FieldLabel>
                        Attachments (optional)
                    </FieldLabel>

                    <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" multiple
                        aria-label="Delivery attachments" onChange={event => setAttachments(Array.from(event.target.files || []))}
                        className="block w-full text-sm text-slate-600" />
                    <p className="mt-2 text-xs text-slate-500">Delivery notes, labels or vehicle photos (10 MB per file).</p>
                    {attachments.map((file, index) => <p key={index} className="mt-1 text-xs text-slate-700">{file.name}</p>)}
                </div>
            </div>
        </section>
    );
}

function InternalUse() {
    const { user } = useDeliveryCheck();
    return (
        <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-3">
                <FiBox className="h-4 w-4 text-indigo-600" />

                <h2 className="text-xs font-bold text-slate-900">
                    Internal Use
                    <span className="ml-1 font-medium text-indigo-600">
                        (Automatic)
                    </span>
                </h2>
            </div>

            <div className="grid grid-cols-1 divide-y divide-slate-100 md:grid-cols-3 md:divide-x md:divide-y-0">
                <div className="px-5 py-3">
                    <p className="text-xs font-semibold text-slate-700">
                        Delivery ID
                    </p>

                    <p className="mt-1 text-[11px] text-slate-400">
                        Will be generated after completion
                    </p>
                </div>

                <div className="px-5 py-3">
                    <p className="text-xs font-semibold text-slate-700">
                        Recorded By
                    </p>

                    <p className="mt-1 text-[11px] text-slate-500">
                        {user?.fullName || "Recorded when saved"}
                    </p>
                </div>

                <div className="px-5 py-3">
                    <p className="text-xs font-semibold text-slate-700">
                        Record Created On
                    </p>

                    <p className="mt-1 text-[11px] text-slate-500">
                        {new Date().toLocaleString("en-GB")}
                    </p>
                </div>
            </div>
        </section>
    );
}

function DeliverySummary() {
    const { delivery, products } = useDeliveryCheck();

    return (
        <aside className="min-w-0 w-full self-start break-words rounded-xl border border-slate-200 bg-white">
            <div className="border-b border-slate-100 px-5 py-4">
                <h2 className="text-sm font-bold text-slate-900">
                    Delivery Summary
                </h2>
            </div>

            <div className="space-y-4 p-5">
                <div className="flex items-start gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                        <FiUser className="h-4 w-4" />
                    </div>

                    <div>
                        <p className="text-[10px] text-slate-500">
                            Supplier
                        </p>

                        <div className="flex items-center gap-2">
                            <p className="text-xs font-bold text-slate-800">
                                {delivery.supplier}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="flex items-start gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                        <FiCalendar className="h-4 w-4" />
                    </div>

                    <div>
                        <p className="text-[10px] text-slate-500">
                            Delivery Date & Time
                        </p>

                        <p className="text-xs font-bold text-slate-800">
                            {delivery.deliveryDateTime
                                ? new Date(
                                      delivery.deliveryDateTime
                                  ).toLocaleString("en-GB", {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                  })
                                : "-"}
                        </p>
                    </div>
                </div>

                <div className="flex items-start gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                        <FiFileText className="h-4 w-4" />
                    </div>

                    <div>
                        <p className="text-[10px] text-slate-500">
                            Delivery Note
                        </p>

                        <p className="text-xs font-bold text-slate-800">
                            {delivery.deliveryNote || "-"}
                        </p>
                    </div>
                </div>

                <div className="flex items-start gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                        <FiFileText className="h-4 w-4" />
                    </div>

                    <div>
                        <p className="text-[10px] text-slate-500">
                            PO Number
                        </p>

                        <p className="text-xs font-bold text-slate-800">
                            {delivery.poNumber || "-"}
                        </p>
                    </div>
                </div>

                <div className="flex items-start gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                        <FiTruck className="h-4 w-4" />
                    </div>

                    <div>
                        <p className="text-[10px] text-slate-500">
                            Vehicle Registration
                        </p>

                        <p className="text-xs font-bold text-slate-800">
                            {delivery.vehicleRegistration || "-"}
                        </p>
                    </div>
                </div>

                <div className="flex items-start gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                        <FiUser className="h-4 w-4" />
                    </div>

                    <div>
                        <p className="text-[10px] text-slate-500">
                            Driver
                        </p>

                        <p className="text-xs font-bold text-slate-800">
                            {delivery.driverName || "-"}
                        </p>
                    </div>
                </div>

                <div className="my-2 border-t border-slate-100" />

                <div>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <FiBox className="h-4 w-4 text-indigo-600" />

                            <h3 className="text-xs font-bold text-slate-900">
                                Products ({products.length})
                            </h3>
                        </div>
                    </div>

                    <div className="mt-3 space-y-4">
                        {products.map((product, index) => (
                            <div
                                key={product.id}
                                className="flex gap-3"
                            >
                                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[10px] font-bold text-slate-600">
                                    {index + 1}
                                </div>

                                <div className="min-w-0">
                                    <p className="text-xs font-bold text-slate-800">
                                        {product.description ||
                                            "Product"}
                                    </p>

                                    <p className="mt-0.5 text-[10px] text-slate-500">
                                        {product.category || "Category"}
                                    </p>

                                    <p className="mt-1 text-xs font-bold text-slate-800">
                                        {product.quantity}{" "}
                                        {product.unit}
                                    </p>

                                    <p className="text-[10px] text-slate-500">
                                        Batch:{" "}
                                        {product.batchNumber || "-"}
                                    </p>

                                    <p className="text-[10px] text-slate-500">
                                        Use-by:{" "}
                                        {product.useByDate || "-"}
                                    </p>

                                    <p className="text-[10px] text-slate-500">
                                        Best before:{" "}
                                        {product.bestBefore || "-"}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="rounded-lg bg-indigo-50 p-3">
                    <div className="flex gap-2">
                        <FiInfo className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" />

                        <p className="text-[10px] leading-4 text-indigo-700">
                            This information will be linked to the
                            delivery check and used in the Compliance
                            Centre for delivery and traceability records.
                        </p>
                    </div>
                </div>
            </div>
        </aside>
    );
}

function DeliveryCheckForm() {
    const { continueToCheck, cancel, isSaving, error, detailsSaved } = useDeliveryCheck();
    return (
        <div className="min-h-screen">
            <DeliveryProgress />

            <main className="mx-auto max-w-[1500px] py-10">
                <div className="grid min-w-0 grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
                    <div className="min-w-0 space-y-4">
                        {error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
                        <fieldset disabled={isSaving || detailsSaved} className="min-w-0 w-full space-y-4">
                        <SupplierInformation />

                        <DeliveryInformation />

                        <ProductsTable />

                        <AdditionalInformation />

                        <InternalUse />
                        </fieldset>

                        <div className="flex flex-wrap items-center justify-between gap-3 pb-2">
                            <button
                                type="button"
                                onClick={cancel} disabled={isSaving}
                                className="rounded-lg border border-slate-200 bg-white px-6 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                onClick={continueToCheck} disabled={isSaving}
                                className="flex items-center gap-2 rounded-lg bg-indigo-600 px-6 py-3 disabled:opacity-50 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2"
                            >
                                {isSaving ? "Saving delivery..." : "Continue to Delivery Check"}
                                <FiArrowRight className="h-4 w-4" />
                            </button>
                        </div>
                    </div>

                    <DeliverySummary />
                </div>
            </main>
        </div>
    );
}

function DeliveryCheckPage() {
    return (
        <DeliveryCheckProvider>
            <DefaultLayout>
                <DeliveryCheckForm />
            </DefaultLayout>
        </DeliveryCheckProvider>
    );
}

export default DeliveryCheckPage;
